import React, { useEffect, useMemo, useRef, useState } from "react";
import { X, Paperclip, HardDrive, Cloud, Trash2, Loader2, Sparkles, ChevronDown } from "lucide-react";
import { api } from "../../api";
import { COLUMNS, avatarColorFor, initialsFor } from "../tasks/constants";

const PRIORITIES = ["low", "normal", "medium", "high", "urgent"];
const REMINDERS  = [
  { v: "none",    label: "No reminder" },
  { v: "at_due",  label: "At due time" },
  { v: "1_hour",  label: "1 hour before" },
  { v: "1_day",   label: "1 day before" },
  { v: "1_week",  label: "1 week before" },
];
const RECURRENCES = [
  { v: "none",    label: "Does not repeat" },
  { v: "daily",   label: "Every day" },
  { v: "weekly",  label: "Every week" },
  { v: "monthly", label: "Every month" },
  { v: "yearly",  label: "Every year" },
];
const BILLING_TYPES = [
  { v: "non_billable", label: "Non-billable" },
  { v: "billable",     label: "Billable · hourly" },
  { v: "fixed",        label: "Fixed price" },
  { v: "hourly",       label: "Hourly rate" },
];

/**
 * Full "Create Task" modal — matches the required Zoho-style spec with 15 fields,
 * AI title/description suggestion, HEXA Files picker, and desktop upload.
 */
export default function CreateTaskModal({
  isOpen,
  onClose,
  projects = [],
  defaultProject,          // string project name to pre-select
  onCreated,               // callback(newTask)
  currentUser,
  teamOptions = [],        // [{address, name}]
}) {
  const [form, setForm] = useState(() => empty(defaultProject, currentUser));
  const [saving, setSaving] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [showFilesPicker, setShowFilesPicker] = useState(false);
  const [tagInput, setTagInput] = useState("");
  const fileRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setForm(empty(defaultProject, currentUser));
      setTagInput("");
    }
  }, [isOpen, defaultProject, currentUser]);

  if (!isOpen) return null;

  const setF = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const addTag = () => {
    const t = tagInput.trim().toLowerCase();
    if (!t) return;
    if (!form.tags.includes(t)) setF("tags", [...form.tags, t]);
    setTagInput("");
  };
  const removeTag = (t) => setF("tags", form.tags.filter(x => x !== t));

  const toggleTeam = (addr) => {
    const has = form.team.includes(addr);
    setF("team", has ? form.team.filter(x => x !== addr) : [...form.team, addr]);
  };

  const uploadDesktop = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { alert("Max 5MB per file"); return; }
    const b64 = await new Promise((res, rej) => {
      const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(file);
    });
    setF("attachments", [
      ...form.attachments,
      { id: `local-${Date.now()}`, name: file.name, mime: file.type, size: file.size, data: b64 },
    ]);
  };

  const aiSuggest = async () => {
    if (!form.project) { alert("Pick a project first — AI needs it for context."); return; }
    setAiBusy(true);
    try {
      const { data } = await api.post("/projects/ai/suggest-tasks", {
        project_name: form.project,
        project_description: form.description || "",
        count: 1,
      });
      const t = (data?.tasks || [])[0];
      if (t) {
        setForm(f => ({
          ...f,
          title: t.title || f.title,
          status: normalizeStatus(t.status) || f.status,
          priority: t.priority || f.priority,
          work_hours: t.work_hours || f.work_hours,
          tags: t.tags?.length ? Array.from(new Set([...(f.tags || []), ...t.tags.map(x => (""+x).toLowerCase())])) : f.tags,
        }));
      } else {
        alert("AI didn't return a suggestion — try again.");
      }
    } catch (e) { alert(e?.response?.data?.detail || "AI unavailable"); }
    setAiBusy(false);
  };

  const save = async () => {
    if (!form.title.trim()) { alert("Task title is required"); return; }
    setSaving(true);
    const payload = {
      title: form.title.trim(),
      description: form.description || "",
      project: form.project || "Inbox",
      status: form.status,
      priority: form.priority,
      due: form.due || "",
      start_date: form.start_date || "",
      end_date: form.due || "",
      tags: form.tags,
      owner_name: form.owner_name || (currentUser?.name || (currentUser?.address || "").split("@")[0]),
      team: form.team,
      work_hours: Number(form.work_hours) || 0,
      reminder: form.reminder,
      recurrence: form.recurrence,
      billing_type: form.billing_type,
      attachments: form.attachments,
      hexa_file_refs: form.hexa_file_refs,
      assignees: form.team.length
        ? form.team.map(addr => ({
            name: addr,
            initials: initialsFor(addr),
            color: avatarColorFor(addr),
          }))
        : [{
            name: currentUser?.address || "You",
            initials: initialsFor(currentUser?.address || "You"),
            color: avatarColorFor(currentUser?.address || "You"),
          }],
    };
    try {
      const { data } = await api.post("/tasks", payload);
      onCreated?.(data);
      onClose?.();
    } catch (e) { alert(e?.response?.data?.detail || "Could not create task"); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-[500] flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div className="bg-white text-slate-800 w-full max-w-3xl max-h-[92vh] rounded-lg shadow-2xl overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()} data-testid="create-task-modal">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-indigo-600 mono">HEXA · New Task</div>
            <h2 className="text-lg font-bold text-slate-800">Create Task</h2>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={aiSuggest} disabled={aiBusy} data-testid="task-ai-suggest"
              className="text-xs font-semibold px-3 py-1.5 rounded border border-purple-300 bg-purple-50 text-purple-700 hover:bg-purple-100 flex items-center gap-1 disabled:opacity-60">
              {aiBusy ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />} AI Suggest
            </button>
            <button onClick={onClose} className="p-1.5 hover:bg-slate-100 rounded text-slate-500"><X size={16} /></button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {/* Row 1: Project + Task name */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="md:col-span-1">
              <L>Project name <span className="text-red-500">*</span></L>
              <select value={form.project} onChange={(e) => setF("project", e.target.value)} data-testid="ct-project"
                className={FIELD}>
                <option value="Inbox">Inbox</option>
                {projects.map(p => <option key={p.id} value={p.title || p.name}>{p.title || p.name}</option>)}
              </select>
            </div>
            <div className="md:col-span-2">
              <L>Task name <span className="text-red-500">*</span></L>
              <input value={form.title} onChange={(e) => setF("title", e.target.value)} data-testid="ct-title"
                autoFocus placeholder="What needs to be done?" className={FIELD} />
            </div>
          </div>

          {/* Description */}
          <div>
            <L>Description</L>
            <textarea rows={3} value={form.description} onChange={(e) => setF("description", e.target.value)}
              placeholder="Details, requirements, references…"
              className={`${FIELD} resize-none`} data-testid="ct-desc" />
          </div>

          {/* Attachments */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <L>Attachment</L>
              <div className="flex gap-2">
                <button onClick={() => fileRef.current?.click()} data-testid="ct-attach-desktop"
                  className="text-[11px] font-semibold px-2 py-1 rounded border border-slate-200 hover:bg-slate-50 flex items-center gap-1 text-slate-700">
                  <HardDrive size={11} /> Desktop
                </button>
                <button onClick={() => setShowFilesPicker(v => !v)} data-testid="ct-attach-hexa"
                  className="text-[11px] font-semibold px-2 py-1 rounded border border-indigo-200 hover:bg-indigo-50 flex items-center gap-1 text-indigo-700">
                  <Cloud size={11} /> HEXA Files
                </button>
                <input ref={fileRef} type="file" className="hidden" onChange={(e) => uploadDesktop(e.target.files?.[0])} />
              </div>
            </div>
            {showFilesPicker && <HexaFilesPicker onPick={(file) => {
              setF("hexa_file_refs", [...form.hexa_file_refs, { id: file.id, name: file.name || file.filename, mime: file.mime || "" }]);
              setShowFilesPicker(false);
            }} />}
            {(form.attachments.length + form.hexa_file_refs.length) === 0 && (
              <div className="text-[11px] text-slate-400 border border-dashed border-slate-200 rounded p-3 text-center">
                No attachments — upload from desktop or pick from HEXA Files.
              </div>
            )}
            <div className="space-y-1">
              {form.attachments.map(a => (
                <AttachRow key={a.id} label={a.name} sublabel={`${Math.round((a.size||0)/1024)} KB · desktop`}
                  onRemove={() => setF("attachments", form.attachments.filter(x => x.id !== a.id))} />
              ))}
              {form.hexa_file_refs.map(a => (
                <AttachRow key={a.id} label={a.name} sublabel="HEXA Files"
                  onRemove={() => setF("hexa_file_refs", form.hexa_file_refs.filter(x => x.id !== a.id))} />
              ))}
            </div>
          </div>

          {/* Row: Status · Priority · Work hours */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <L>Task list (Status)</L>
              <select value={form.status} onChange={(e) => setF("status", e.target.value)} className={FIELD} data-testid="ct-status">
                {COLUMNS.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
              </select>
            </div>
            <div>
              <L>Priority</L>
              <select value={form.priority} onChange={(e) => setF("priority", e.target.value)} className={FIELD} data-testid="ct-priority">
                {PRIORITIES.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <div>
              <L>Work hours</L>
              <input type="number" min="0" step="0.5" value={form.work_hours}
                onChange={(e) => setF("work_hours", e.target.value)} className={FIELD} data-testid="ct-hours"
                placeholder="e.g. 4" />
            </div>
          </div>

          {/* Row: Owner · Associate team */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <L>Owner</L>
              <input value={form.owner_name} onChange={(e) => setF("owner_name", e.target.value)}
                placeholder={(currentUser?.address || "").split("@")[0]} className={FIELD} data-testid="ct-owner" />
            </div>
            <div className="md:col-span-2">
              <L>Associate team</L>
              {teamOptions.length === 0 && (
                <input value={form.team.join(", ")} onChange={(e) => setF("team", e.target.value.split(",").map(s => s.trim()).filter(Boolean))}
                  placeholder="comma-separated addresses (e.g. abc@hexawebos.com)" className={FIELD} data-testid="ct-team-text" />
              )}
              {teamOptions.length > 0 && (
                <div className="flex flex-wrap gap-1 border border-slate-200 rounded p-2 bg-white min-h-[38px]">
                  {teamOptions.map(t => {
                    const on = form.team.includes(t.address);
                    return (
                      <button key={t.address} type="button" onClick={() => toggleTeam(t.address)}
                        data-testid={`ct-team-${t.address}`}
                        className={`text-[10px] font-semibold px-2 py-1 rounded-full border ${on ? "bg-indigo-600 text-white border-indigo-600" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}>
                        {t.name || t.address}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Row: Start · Due · Reminder · Recurrence */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div>
              <L>Start date</L>
              <input type="date" value={form.start_date} onChange={(e) => setF("start_date", e.target.value)} className={FIELD} style={{ colorScheme: "light" }} data-testid="ct-start" />
            </div>
            <div>
              <L>Due date</L>
              <input type="date" value={form.due} onChange={(e) => setF("due", e.target.value)} className={FIELD} style={{ colorScheme: "light" }} data-testid="ct-due" />
            </div>
            <div>
              <L>Reminder</L>
              <select value={form.reminder} onChange={(e) => setF("reminder", e.target.value)} className={FIELD} data-testid="ct-reminder">
                {REMINDERS.map(r => <option key={r.v} value={r.v}>{r.label}</option>)}
              </select>
            </div>
            <div>
              <L>Recurrence</L>
              <select value={form.recurrence} onChange={(e) => setF("recurrence", e.target.value)} className={FIELD} data-testid="ct-recurrence">
                {RECURRENCES.map(r => <option key={r.v} value={r.v}>{r.label}</option>)}
              </select>
            </div>
          </div>

          {/* Tags */}
          <div>
            <L>Tags</L>
            <div className="flex flex-wrap gap-1 border border-slate-200 rounded p-2 bg-white">
              {form.tags.map(t => (
                <span key={t} className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700">
                  {t}
                  <button onClick={() => removeTag(t)} className="hover:text-red-500">×</button>
                </span>
              ))}
              <input value={tagInput} onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === ",") { e.preventDefault(); addTag(); } }}
                onBlur={addTag}
                placeholder={form.tags.length ? "" : "Add tags — press Enter"}
                className="flex-1 min-w-[100px] outline-none text-xs px-1 py-1 bg-white text-slate-800"
                data-testid="ct-tag-input" />
            </div>
          </div>

          {/* Billing type */}
          <div>
            <L>Billing type</L>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {BILLING_TYPES.map(b => (
                <button key={b.v} type="button" onClick={() => setF("billing_type", b.v)}
                  data-testid={`ct-billing-${b.v}`}
                  className={`text-xs font-semibold px-3 py-2 rounded border transition ${form.billing_type === b.v ? "bg-indigo-600 text-white border-indigo-600" : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"}`}>
                  {b.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="px-6 py-3 border-t border-slate-100 flex items-center justify-end gap-2 bg-slate-50">
          <button onClick={onClose} className="text-xs font-semibold px-4 py-2 rounded border border-slate-200 text-slate-600 hover:bg-white">Cancel</button>
          <button onClick={save} disabled={saving || !form.title.trim()} data-testid="ct-save"
            className={`text-xs font-semibold px-4 py-2 rounded flex items-center gap-1 ${form.title.trim() && !saving ? "bg-indigo-600 text-white hover:bg-indigo-700" : "bg-slate-200 text-slate-400 cursor-not-allowed"}`}>
            {saving ? <Loader2 size={12} className="animate-spin" /> : null} Create Task
          </button>
        </div>
      </div>
    </div>
  );
}

const FIELD = "w-full border border-slate-200 rounded px-3 py-2 text-sm bg-white text-slate-800 placeholder:text-slate-400 outline-none focus:border-indigo-400";
function L({ children }) { return <label className="text-[10px] uppercase tracking-widest text-slate-500 mb-1 block mono">{children}</label>; }

function AttachRow({ label, sublabel, onRemove }) {
  return (
    <div className="flex items-center gap-2 p-2 border border-slate-200 rounded bg-white">
      <div className="w-8 h-8 bg-indigo-50 text-indigo-600 rounded flex items-center justify-center"><Paperclip size={12} /></div>
      <div className="flex-1 min-w-0">
        <div className="text-xs font-semibold truncate">{label}</div>
        <div className="text-[10px] text-slate-500">{sublabel}</div>
      </div>
      <button onClick={onRemove} className="p-1 hover:bg-slate-100 rounded text-red-500"><Trash2 size={11} /></button>
    </div>
  );
}

function HexaFilesPicker({ onPick }) {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/files");
        setFiles(data || []);
      } catch { setFiles([]); }
      setLoading(false);
    })();
  }, []);
  return (
    <div className="border border-indigo-200 rounded bg-indigo-50/40 p-2 mt-1 mb-2 max-h-40 overflow-y-auto" data-testid="hexa-files-picker">
      <div className="text-[10px] uppercase tracking-widest text-indigo-600 mb-1 mono">Pick from HEXA Files</div>
      {loading && <div className="text-xs text-slate-400 py-2">Loading files…</div>}
      {!loading && files.length === 0 && (
        <div className="text-xs text-slate-500 py-2">No files in HEXA Files yet.</div>
      )}
      {files.map(f => (
        <button key={f.id} onClick={() => onPick(f)} data-testid={`hexa-file-${f.id}`}
          className="w-full text-left px-2 py-1.5 rounded hover:bg-white flex items-center gap-2 text-xs">
          <Paperclip size={11} className="text-indigo-500" />
          <span className="flex-1 truncate">{f.name || f.filename || "file"}</span>
          <span className="text-[10px] text-slate-400">{f.mime || ""}</span>
        </button>
      ))}
    </div>
  );
}

function empty(defaultProject, currentUser) {
  return {
    project: defaultProject || "Inbox",
    title: "",
    description: "",
    attachments: [],
    hexa_file_refs: [],
    status: "todo",
    priority: "normal",
    work_hours: "",
    owner_name: (currentUser?.name || (currentUser?.address || "").split("@")[0] || ""),
    team: [],
    start_date: "",
    due: "",
    tags: [],
    reminder: "none",
    recurrence: "none",
    billing_type: "non_billable",
  };
}

function normalizeStatus(s) {
  if (!s) return "";
  const v = String(s).toLowerCase().replace(/\s+/g, "_");
  const map = { doing: "in_progress", todo: "todo", yet_to_start: "yet_to_start", in_progress: "in_progress", on_hold: "on_hold", completed: "completed", done: "completed", backlog: "todo" };
  return map[v] || v;
}
